/**
 * GeoLocationPicker — Reusable geographic-community typeahead.
 *
 * Three surfaces use this: Account Settings (Home + Operational
 * Community), Community Settings (Primary Location), and — eventually —
 * Project Settings → Basics. Earlier each surface had its own
 * implementation, which drifted across versions: different scoring
 * algorithms, different fallbacks for missing entities, even one
 * surface with no typeahead at all (a raw GUID input).
 *
 * This component is the canonical UI. Callers pass:
 *   - the list of community entities + relationships (already loaded
 *     in the calling context)
 *   - the current GUID value
 *   - an onChange handler
 *   - a placeholder string
 *
 * The component handles its own query state, suggestion rendering,
 * label resolution, and clear-token behavior. Search and label logic
 * comes from `src/utils/geoLocationSearch.ts` so the algorithm stays
 * single-sourced even if a fourth caller someday wants its own custom
 * UI but the same backing search.
 *
 * Tokens-only colors and logical properties throughout (RTL safe).
 */

import React, { useMemo, useState } from 'react';
import {
  searchGeoLocations,
  getGeoLocationLabel,
} from '../../utils/geoLocationSearch';
import styles from './GeoLocationPicker.module.css';

export interface GeoLocationPickerProps {
  /** Currently selected community GUID (or empty string when unset). */
  value: string;
  /** Called with a new GUID when the user picks a suggestion, or '' on clear. */
  onChange: (guid: string) => void;
  /** All community entities (geo + org + community). Filtered to geo internally. */
  entities: any[];
  /** Parent/child community relationships (for ancestor path display). */
  relationships: any[];
  /** Placeholder text for the input when no value is selected. */
  placeholder?: string;
  /** Disable the picker entirely (read-only mode). */
  disabled?: boolean;
  /** Optional className passed through for layout positioning. */
  className?: string;
  /**
   * Opt-in: additional non-geo communities to make selectable/searchable
   * (e.g. org communities the user is affiliated with). Omitted → unchanged
   * geo-only behavior. Used by the default-community picker so org communities
   * are selectable without affecting the geo-anchored Home/Operational pickers.
   */
  extraSelectable?: { guid: string; name: string }[];
}

const GeoLocationPicker: React.FC<GeoLocationPickerProps> = ({
  value,
  onChange,
  entities,
  relationships,
  extraSelectable = [],
  placeholder = 'Type a city, state, or country…',
  disabled = false,
  className,
}) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const suggestions = useMemo(
    () => {
      const geo = searchGeoLocations({ query, entities, relationships });
      const q = query.trim().toLowerCase();
      if (!q || extraSelectable.length === 0) return geo;
      const seen = new Set(geo.map((g) => g.guid.toUpperCase()));
      const extras = extraSelectable
        .filter((x) => x.name.toLowerCase().includes(q) && !seen.has(x.guid.toUpperCase()))
        .map((x) => ({ guid: x.guid, display: x.name }));
      return [...geo, ...extras];
    },
    [query, entities, relationships, extraSelectable],
  );

  const selectedLabel = useMemo(
    () => {
      if (value) {
        const extra = extraSelectable.find((x) => x.guid.toUpperCase() === value.toUpperCase());
        if (extra) return extra.name;
      }
      return getGeoLocationLabel({ guid: value, entities, relationships });
    },
    [value, entities, relationships, extraSelectable],
  );

  const handleClear = () => {
    onChange('');
    setQuery('');
    setShowSuggestions(false);
  };

  const handlePick = (guid: string) => {
    onChange(guid);
    setQuery('');
    setShowSuggestions(false);
  };

  return (
    <div className={`${styles.root} ${className || ''}`}>
      <div className={`${styles.inputWrap} ${disabled ? styles.inputWrapDisabled : ''}`}>
        {value && (
          <div className={styles.token} title={selectedLabel}>
            <span className={styles.tokenLabel}>{selectedLabel}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.tokenClear}
                onClick={handleClear}
                aria-label="Clear selection"
              >
                ×
              </button>
            )}
          </div>
        )}
        <input
          className={styles.input}
          value={query}
          disabled={disabled}
          placeholder={value ? 'Type to change…' : placeholder}
          onChange={(e) => {
            const v = e.target.value;
            // If a token is currently selected and the user starts typing,
            // clear the token so the input becomes the active query field.
            if (value && v.trim().length > 0) onChange('');
            setQuery(v);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          // Delay blur so a click on a suggestion has time to register.
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className={styles.suggestions} role="listbox">
          {suggestions.map((s) => (
            <div
              key={s.guid}
              className={styles.suggestion}
              role="option"
              aria-selected="false"
              // mousedown (not click) so it fires before the input's blur
              // handler hides the suggestions list.
              onMouseDown={() => handlePick(s.guid)}
            >
              {s.display}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeoLocationPicker;
